// ③ 무대 디자인 (1차): 디자인 조건 → AI 디자인 방향 → 시안 여러 장 → 하나 확정 → 다른 방향에서 보기 → 요소 합의로
// 위에서 아래로 일의 순서대로 놓고, 끝난 구획은 접는다.
import { ANGLE_VIEWS, byId, STAGE_PRESETS, TONES } from "../../shared/knowledge";
import type { Cut, Project } from "../../shared/types";
import { icon } from "../icons";
import { announce, api, busy, clear, devDetails, emptyState, eyebrow, fileName, fmtDate, genState, h, helpTip, mediaUrl, menu, nudgeQueued, openViewer, redraw, segmented, toast, withCost, type ViewerItem } from "../lib";
import { stageIcon } from "../stage-icons";
import { refreshMe, state } from "../store";
import { assetsPanel } from "./assets";
import { openFixDialog } from "./fix-dialog";
import type { Ctx } from "./project";

let pollTimer: any = null;
const ABC = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

export async function renderStage(body: HTMLElement, ctx: Ctx) {
  clearTimeout(pollTimer);
  let cuts = await api<Cut[]>(`/api/projects/${ctx.p.id}/cuts?phase=stage`);
  const admin = Boolean(state.me?.admin);
  const selected = new Set<string>();
  for (const d of ctx.p.data.stage?.directions || []) selected.add(d.id);
  // 사용자가 직접 열거나 접은 구획은 그대로 둔다 (null = 자동)
  let openA: boolean | null = null;
  let openB: boolean | null = null;
  let openC: boolean | null = null;

  const resultBox = h("div");
  const secA = h("details", { class: "card step-section" });
  const secB = h("details", { class: "card step-section" });
  const secC = h("details", { class: "card step-section" });
  const per = () => state.config?.models.fast?.credits["2K"] ?? 1;
  const dirs = () => ctx.p.data.stage?.directions || [];
  const stageCuts = () => cuts.filter((c) => c.phase === "stage");

  // 컨셉은 쓰고 0.5초 뒤에 저장한다. 방향을 받기 전에는 남은 저장을 바로 보낸다 (새 문구가 빠지지 않게)
  let conceptTimer: any;
  const saveConcept = async () => {
    clearTimeout(conceptTimer);
    if (concept.value === (ctx.p.data.stage?.concept || "")) return;
    ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/stage`, { method: "PATCH", body: { concept: concept.value } }));
  };

  /** 시안 이름표: 지금 방향 순서대로 A~D, 그 밖의 이름은 만든 순서대로 이어서 */
  function letters(): Map<string, string> {
    const m = new Map<string, string>();
    for (const d of dirs()) if (!m.has(d.name)) m.set(d.name, ABC[m.size] || "?");
    for (const c of [...stageCuts()].sort((a, b) => a.createdAt.localeCompare(b.createdAt))) {
      const k = c.direction || "";
      if (!m.has(k)) m.set(k, ABC[m.size] || "?");
    }
    return m;
  }
  const cutName = (c: Cut, L: Map<string, string>) => `시안 ${L.get(c.direction || "") || ""}${c.kind === "fix" ? ` · 고친 시안 ${c.depth}차` : ""}`;
  const sortedStageCuts = (L: Map<string, string>) =>
    [...stageCuts()].sort((a, b) => (L.get(a.direction || "") || "").localeCompare(L.get(b.direction || "") || "") || a.depth - b.depth || a.createdAt.localeCompare(b.createdAt));

  // ── 행동 ──
  const askDirections = (btn: HTMLButtonElement) =>
    busy(
      btn,
      "방향을 짜는 중",
      async () => {
        await saveConcept();
        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/stage/directions`, { body: { count: 4 } }));
        selected.clear();
        for (const d of dirs()) selected.add(d.id);
        openA = null;
        openB = null;
        drawAll();
      },
      { timer: true },
    );

  const drawSians = (btn: HTMLButtonElement) =>
    busy(btn, "보내는 중", async () => {
      if (!selected.size) throw new Error("디자인 방향을 하나 이상 골라 주세요.");
      const made = await api<Cut[]>(`/api/projects/${ctx.p.id}/stage/cuts`, { body: { directionIds: [...selected], model: "fast", size: "2K", aspect: "16:9" } });
      cuts = [...made, ...cuts];
      await refreshMe();
      openB = null;
      drawAll();
      poll();
      toast("시안을 그리기 시작했어요", "info");
    });

  const skip = (btn: HTMLButtonElement | null) =>
    busy(btn, "", async () => {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/stage`, { method: "PATCH", body: { skipped: true } }));
      ctx.step("elements");
    });

  const approve = (c: Cut, btn: HTMLButtonElement, after?: () => void) =>
    busy(btn, "", async () => {
      ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/stage/approve`, { body: { cutId: c.id } }));
      toast("무대를 확정했어요", "ok");
      drawAll();
      if (after) after();
      else resultBox.scrollIntoView({ behavior: "smooth", block: "start" });
    });

  // ── 결과 요약 (확정했을 때만, 맨 위) ──
  function drawResult() {
    clear(resultBox);
    const st = ctx.p.data.stage;
    if (!st?.approvedKey) return;
    const approvedCut = cuts.find((c) => c.id === st.approvedCutId);
    const angles = cuts.filter((c) => c.phase === "stage-angle");
    const n = ANGLE_VIEWS.length - 1;
    const more = h("button", { class: "btn", type: "button" }, icon("refresh", 16), withCost(`다른 방향 ${n}장 그리기`, n));
    more.addEventListener("click", () =>
      busy(more, "보내는 중", async () => {
        const made = await api<Cut[]>(`/api/projects/${ctx.p.id}/stage/angles`, { body: { views: ["front", "side", "aerial"] } });
        cuts = [...made, ...cuts];
        await refreshMe();
        drawResult();
        poll();
      }),
    );
    const L = letters();
    resultBox.append(
      h(
        "div",
        { class: "card result-card", "data-gallery": "approved" },
        h("div", { class: "result-media" }, h("img", { class: "zoomable", src: mediaUrl(st.approvedKey), alt: approvedCut ? `확정한 무대 · ${cutName(approvedCut, L)}` : "확정한 무대", decoding: "async" })),
        h(
          "div",
          { class: "result-info" },
          h("span", { class: "chip p1" }, icon("check", 14), "확정한 무대"),
          h("h2", null, approvedCut ? `${cutName(approvedCut, L)} · ${approvedCut.direction || ""}` : "확정한 무대"),
          st.concept ? h("p", { class: "small muted one-line", title: st.concept }, st.concept) : null,
          angles.length
            ? h(
                "div",
                { class: "angle-grid" },
                angles.map((c) =>
                  h(
                    "figure",
                    { class: "angle" },
                    // 원본이 커서 늦게 뜬다. 뜨기 전에는 반짝이는 바탕을 보인다 (작은 그림 따로 만들기는 서버 일)
                    c.status === "done" && c.mediaKey
                      ? h("div", { class: "thumb wide skeleton" }, h("img", { class: "zoomable", src: mediaUrl(c.mediaKey), alt: `확정 무대 · ${c.direction || ""}`, loading: "lazy", decoding: "async", onload: (e: Event) => (e.currentTarget as HTMLElement).parentElement?.classList.remove("skeleton") }))
                      : h("div", { class: "thumb wide" }, genState(c, "")),
                    h("figcaption", { class: "xs muted" }, c.direction || ""),
                  ),
                ),
              )
            : h("div", { class: "stack tight" }, h("div", null, more), h("p", { class: "xs muted" }, "다른 방향도 그려 두면 연출컷에서 무대가 흔들리지 않아요")),
        ),
      ),
    );
  }

  // ── A. 디자인 조건 ──
  const concept = h("textarea", { rows: 2, maxlength: 600, placeholder: "예: 충청의 산과 물결, 하나 되는 빛, 미래로 뻗는 길" }, ctx.p.data.stage?.concept || "") as HTMLTextAreaElement;
  concept.addEventListener("input", () => {
    clearTimeout(conceptTimer);
    conceptTimer = setTimeout(() => void saveConcept().catch((err) => toast(err instanceof Error ? err.message : String(err), "err")), 500);
  });
  const formGrid = h("div", { class: "stage-grid four" });
  const drawForms = () => redraw(formGrid, drawFormCards);
  const drawFormCards = () => {
    clear(formGrid);
    const cur = ctx.p.data.stage?.form || "";
    for (const s of [{ id: "", ko: "AI에게 맡기기", hint: "모양까지 여러 안으로" }, ...STAGE_PRESETS.filter((x) => !x.spot)]) {
      const pic = h("div", { class: "stage-icon" });
      if (s.id) pic.innerHTML = stageIcon(s.id);
      else pic.append(h("span", { class: "stage-icon-ai" }, icon("sparkles", 28)));
      formGrid.append(
        h(
          "button",
          {
            type: "button",
            class: `stage-card${cur === s.id ? " on" : ""}`,
            "aria-pressed": cur === s.id ? "true" : "false",
            "data-k": `form-${s.id || "ai"}`,
            title: s.hint || null,
            onclick: async () => {
              try {
                ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/stage`, { method: "PATCH", body: { form: s.id } }));
                drawForms();
                drawSummaryA();
              } catch (err) {
                toast(err instanceof Error ? err.message : String(err), "err");
              }
            },
          },
          pic,
          h("b", null, s.ko),
        ),
      );
    }
  };
  drawForms();
  const tone = segmented(
    TONES.map((t) => ({ id: t.id, ko: t.ko, hint: t.hint })),
    ctx.p.data.scene.tone || "photo",
    async (id) => {
      try {
        ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/scene`, { method: "PATCH", body: { tone: id } }));
        drawSummaryA();
      } catch (err) {
        toast(err instanceof Error ? err.message : String(err), "err");
      }
    },
    "그림 느낌",
    { showHint: true },
  );
  const assets = assetsPanel(ctx, "stage", { embedded: true, onChange: () => drawSummaryA() });
  const sumA = h("span", { class: "sec-sum" });
  const numA = h("span", { class: "sec-num" });
  const summaryA = h("summary", null, numA, h("span", { class: "sec-text" }, h("b", null, "디자인 조건"), sumA), icon("chevron-down", 18));
  summaryA.addEventListener("click", () => (openA = !secA.open));
  secA.append(
    summaryA,
    h(
      "div",
      { class: "section-body stack" },
      ctx.p.data.space.length ? null : h("p", { class: "notice warn small" }, icon("alert", 16), h("span", null, "장소 그림 없이 그려요 · ", h("a", { href: `#/p/${ctx.p.id}/space` }, "장소 정하기"))),
      h("label", null, "컨셉", concept),
      h("div", { class: "field" }, h("span", { class: "field-label" }, "무대 모양", helpTip("참고할 모양을 고르면 그 틀 안에서 디자인을 바꿔 가며 그려요. 'AI에게 맡기기'면 모양까지 다양하게 내요.")), formGrid),
      h("div", { class: "field" }, h("span", { class: "field-label" }, "그림 느낌"), tone),
      assets,
    ),
  );
  function drawSummaryA() {
    const st = ctx.p.data.stage;
    const formName = st?.form ? STAGE_PRESETS.find((s) => s.id === st.form)?.ko : "AI에게 맡기기";
    const nAssets = (ctx.p.data.assets || []).filter((a) => a.useStage).length;
    sumA.textContent = [concept.value.trim() || "컨셉 없음", formName, byId(TONES, ctx.p.data.scene.tone || "photo")?.ko, nAssets ? `소스 ${nAssets}개` : ""].filter(Boolean).join(" · ");
    const done = dirs().length > 0;
    numA.replaceChildren(done ? icon("check", 14, "끝남") : "1");
    numA.classList.toggle("done", done);
  }

  // ── B. 디자인 방향 ──
  function drawB() {
    clear(secB);
    const list = dirs();
    if (!list.length) {
      // 방향이 없어도 구획은 접힌 채 보인다 (번호가 1 → 3 으로 건너뛰지 않게). 받는 단추는 아래 막대에 있다
      secB.open = openB ?? false;
      const summary = h("summary", null, h("span", { class: "sec-num" }, "2"), h("span", { class: "sec-text" }, h("b", null, "디자인 방향"), h("span", { class: "sec-sum" }, "컨셉을 적고 방향을 받아 보세요")), icon("chevron-down", 18));
      summary.addEventListener("click", () => (openB = !secB.open));
      secB.append(summary, h("div", { class: "section-body" }, h("p", { class: "small muted" }, "아래 '디자인 방향 4개 받기'를 누르면 AI가 컨셉에 맞는 방향을 네 가지 내요. 고른 방향마다 시안이 한 장씩 나와요.")));
      return;
    }
    const hasCuts = stageCuts().length > 0;
    secB.open = openB ?? !hasCuts;
    const sum = h("span", { class: "sec-sum" });
    const syncSum = () => (sum.textContent = `${selected.size}개 고름 · ${list.map((d) => d.name).join(", ")}`);
    syncSum();
    const summary = h("summary", null, h("span", { class: `sec-num${hasCuts ? " done" : ""}` }, hasCuts ? icon("check", 14, "끝남") : "2"), h("span", { class: "sec-text" }, h("b", null, "디자인 방향 ", h("span", { class: "num muted" }, String(list.length))), sum), icon("chevron-down", 18));
    summary.addEventListener("click", () => (openB = !secB.open));
    const again = h("button", { class: "btn small", type: "button", onclick: () => void askDirections(again) }, icon("refresh", 16), "방향 다시 받기");
    secB.append(
      summary,
      h(
        "div",
        { class: "section-body stack" },
        h("div", { class: "row between" }, h("p", { class: "small muted" }, "그릴 방향을 골라요. 고른 만큼 시안이 나와요"), again),
        h(
          "div",
          { class: "option-grid dir-grid" },
          list.map((d) => {
            const form = STAGE_PRESETS.find((s) => s.id === d.form);
            const btn: HTMLButtonElement = h(
              "button",
              {
                type: "button",
                class: `option-card dir-card${selected.has(d.id) ? " on" : ""}`,
                "aria-pressed": selected.has(d.id) ? "true" : "false",
                onclick: () => {
                  if (selected.has(d.id)) selected.delete(d.id);
                  else selected.add(d.id);
                  const on = selected.has(d.id);
                  btn.classList.toggle("on", on);
                  btn.setAttribute("aria-pressed", on ? "true" : "false");
                  syncSum();
                  drawBar();
                },
              },
              h("span", { class: "t" }, d.name),
              h("span", { class: "d" }, d.summary),
              form ? h("span", { class: "chip" }, form.ko) : null,
            );
            return h("div", { class: "dir-item" }, btn, admin ? devDetails("전문가용 · 그림 모델에 가는 문장", h("code", null, d.en)) : null);
          }),
        ),
      ),
    );
  }

  // ── C. 시안 ──
  function drawC() {
    clear(secC);
    const L = letters();
    const list = sortedStageCuts(L);
    const approvedNow = Boolean(ctx.p.data.stage?.approvedKey);
    // 확정한 뒤에는 시안 목록을 접는다 (펼쳐 보면 그대로 열려 있다)
    secC.open = openC ?? !approvedNow;
    // 위의 두 구획과 같은 모양: 번호 원 + 제목 + 요약 + chevron
    const nDone = list.filter((c) => c.status === "done").length;
    const sumText = approvedNow ? "확정한 시안은 위에 있어요" : list.length ? `${nDone}장 다 됨 · 하나를 확정해 주세요` : dirs().length ? "디자인 방향을 고르면 그릴 수 있어요" : "디자인 방향을 받으면 그릴 수 있어요";
    const summary = h(
      "summary",
      null,
      h("span", { class: `sec-num${approvedNow ? " done" : ""}` }, approvedNow ? icon("check", 14, "끝남") : "3"),
      h("span", { class: "sec-text" }, h("b", null, "시안 ", h("span", { class: "num muted" }, String(list.length))), h("span", { class: "sec-sum" }, sumText)),
      icon("chevron-down", 18),
    );
    summary.addEventListener("click", () => (openC = !secC.open));
    const sectionBody = h("div", { class: "section-body" });
    secC.append(summary, sectionBody);
    if (!list.length) {
      sectionBody.append(emptyState({ icon: "stage", title: "아직 시안이 없어요", body: dirs().length ? "디자인 방향을 고르면 그릴 수 있어요" : "디자인 방향을 받으면 시안을 그릴 수 있어요", compact: true }));
      return;
    }
    const grid = h("div", { class: "cut-grid" });
    for (const c of list) {
      const approved = ctx.p.data.stage?.approvedCutId === c.id;
      const name = cutName(c, L);
      const done = c.status === "done" && c.mediaKey;
      const approveBtn = h("button", { class: "btn small", type: "button" }, icon("check", 16), "이 시안으로 확정");
      approveBtn.addEventListener("click", () => void approve(c, approveBtn));
      grid.append(
        h(
          "article",
          { class: `card cut-card p1${approved ? " approved" : ""}` },
          h(
            "div",
            { class: "thumb wide" },
            done ? h("img", { src: mediaUrl(c.mediaKey!), alt: `${name} · ${c.direction || ""}`, loading: "lazy", onclick: () => viewer(c) }) : genState(c, "시안"),
            h("span", { class: "chip p1 thumb-tag" }, `시안 ${L.get(c.direction || "") || ""}`),
            approved ? h("span", { class: "thumb-badge p1" }, icon("check", 12), "확정") : null,
          ),
          h(
            "div",
            { class: "cut-meta" },
            h("div", { class: "cut-title" }, h("b", { class: "one-line", title: c.direction || "" }, c.direction || name), h("span", { class: "xs muted num" }, fmtDate(c.createdAt))),
            c.kind === "fix" ? h("p", { class: "xs muted" }, `고친 시안 ${c.depth}차`) : null,
            done
              ? h(
                  "div",
                  { class: "cut-actions" },
                  approved ? h("span", { class: "chip ok" }, icon("check", 14), "확정됨") : approveBtn,
                  h("button", { class: "btn ghost small", type: "button", onclick: () => fixDialog(c) }, icon("pencil", 16), "고치기"),
                  h("span", { class: "spacer" }),
                  h("button", { class: "btn ghost small icon-only", type: "button", "aria-label": `${name} 크게 보기`, title: "크게 보기", onclick: () => viewer(c) }, icon("maximize", 16)),
                )
              : null,
          ),
        ),
      );
    }
    sectionBody.append(grid);
  }

  // ── 아래 행동 막대 ──
  function drawBar() {
    const st = ctx.p.data.stage;
    const list = stageCuts();
    const skipBtn = () => {
      const b = h("button", { class: "btn ghost", type: "button" }, h("span", { class: "hide-sm" }, "무대가 이미 있어요 · "), "건너뛰기");
      b.addEventListener("click", () => void skip(b));
      return b;
    };
    if (st?.approvedKey) {
      const L = letters();
      const ac = cuts.find((c) => c.id === st.approvedCutId);
      const name = `확정: ${ac ? `${cutName(ac, L)} · ${ac.direction || ""}` : "무대"}`;
      // 확정한 무대를 AI가 읽고 ④로 간다. 확정하면 서버가 읽은 결과를 지우므로, 결과가 있으면 이미 읽은 것이다
      const readAndGo = (btn: HTMLButtonElement) =>
        busy(
          btn,
          "무대를 읽는 중",
          async () => {
            ctx.set(await api<Project>(`/api/projects/${ctx.p.id}/analyze`, { body: {} }));
            ctx.step("elements");
          },
          { timer: true },
        );
      if (ctx.p.data.analysis) {
        // 이미 읽었다: '다음'은 그냥 넘어간다. 다시 읽기는 보조로 (AI를 다시 부르고 확인할 목록이 바뀔 수 있다)
        const again = h("button", { class: "btn ghost", type: "button" }, icon("sparkles", 16), h("span", { class: "hide-sm" }, "무대 "), "다시 읽기");
        again.addEventListener("click", () => void readAndGo(again));
        ctx.bar({ status: name, secondary: [again], primary: h("button", { class: "btn primary", type: "button", onclick: () => ctx.step("elements") }, h("span", null, "다음: 요소 합의"), icon("chevron-right", 16)) });
      } else {
        const next = h("button", { class: "btn primary", type: "button" }, h("span", null, "다음: 요소 합의"), icon("chevron-right", 16));
        next.addEventListener("click", () => void readAndGo(next));
        ctx.bar({ status: `${name} · 누르면 AI가 무대를 읽어요 (20~60초)`, primary: next });
      }
    } else if (list.length) {
      const again = h("button", { class: "btn", type: "button", disabled: !selected.size }, h("span", { class: "hide-sm" }, "고른 방향으로 "), withCost("다시 그리기", selected.size * per()));
      again.addEventListener("click", () => void drawSians(again));
      const running = list.some((c) => c.status === "queued" || c.status === "running");
      const anyDone = list.some((c) => c.status === "done");
      // 시안을 그린 뒤에도 건너뛸 길을 ⋯ 메뉴에 남긴다
      const more = menu([{ label: "무대가 이미 있어요 · 건너뛰기", onclick: () => void skip(null) }], "다른 할 일");
      ctx.bar({
        status: !anyDone && running ? "시안을 그리는 중이에요 · 다 되면 하나를 확정해 주세요" : "마음에 드는 시안 하나를 확정해 주세요",
        warn: anyDone,
        secondary: [more, again],
        primary: h("button", { class: "btn primary", type: "button", disabled: true }, h("span", null, h("span", { class: "hide-sm" }, "다음: "), "요소 합의"), icon("chevron-right", 16)),
      });
    } else if (dirs().length) {
      const go = h("button", { class: "btn primary", type: "button", disabled: !selected.size }, withCost(`시안 ${selected.size}장 그리기`, selected.size * per()));
      go.addEventListener("click", () => void drawSians(go));
      ctx.bar({ status: selected.size ? `방향 ${selected.size}개 골랐어요` : "방향을 하나 이상 골라 주세요", warn: !selected.size, secondary: [skipBtn()], primary: go });
    } else {
      const ask = h("button", { class: "btn primary", type: "button" }, icon("sparkles"), "디자인 방향 4개 받기");
      ask.addEventListener("click", () => void askDirections(ask));
      // 장소 그림이 없다는 안내는 디자인 조건 맨 위에 이미 있으니 여기서는 다음 할 일만
      ctx.bar({ status: "컨셉을 적고 방향을 받아 보세요", secondary: [skipBtn()], primary: ask });
    }
  }

  function drawAll() {
    drawResult();
    secA.open = openA ?? !dirs().length;
    drawSummaryA();
    drawB();
    drawC();
    drawBar();
  }

  // ── 크게 보기 ──
  function viewer(c: Cut) {
    const L = letters();
    const list = sortedStageCuts(L).filter((x) => x.status === "done" && x.mediaKey);
    const items: ViewerItem[] = list.map((x) => ({
      src: mediaUrl(x.mediaKey!),
      title: `${cutName(x, L)} · ${x.direction || ""}`,
      meta: fmtDate(x.createdAt),
      download: fileName(`${ctx.p.title}_${cutName(x, L)}`, x.mediaKey!),
      actions: ({ close, refresh }) => {
        const approved = ctx.p.data.stage?.approvedCutId === x.id;
        const b = h("button", { class: "btn", type: "button" }, icon("check", 16), "이 시안으로 확정");
        b.addEventListener("click", () => void approve(x, b, refresh));
        return [
          approved ? h("span", { class: "chip ok" }, icon("check", 14), "확정됨") : b,
          h(
            "button",
            {
              class: "btn",
              type: "button",
              onclick: () => {
                close();
                fixDialog(x);
              },
            },
            icon("pencil", 16),
            "고치기",
          ),
        ];
      },
      details: admin ? () => h("pre", { class: "prompt" }, x.prompt) : undefined,
    }));
    openViewer(items, Math.max(0, list.findIndex((x) => x.id === c.id)));
  }

  /** 시안 고치기: 고칠 곳을 글로 짚는다 (최대 3군데). 보내는 본문은 { fixes: [{ label, type: "wrong", note }] } */
  function fixDialog(c: Cut) {
    const L = letters();
    const cost = state.config?.models[c.model]?.credits[c.size] ?? 1;
    openFixDialog<{ label: string; note: string }>({
      title: "시안 고치기",
      src: mediaUrl(c.mediaKey!),
      alt: `${cutName(c, L)} · ${c.direction || ""}`,
      cost,
      newRow: () => {
        const label = h("input", { placeholder: "예: 지붕", maxlength: 60 });
        const note = h("textarea", { rows: 2, placeholder: "예: 더 낮고 넓게, 물결 모양으로", maxlength: 400 }) as HTMLTextAreaElement;
        return { body: [h("label", null, "어디를", label), h("label", null, "어떻게", note)], value: () => ({ label: label.value.trim(), note: note.value.trim() }), focus: () => label.focus() };
      },
      submit: async (values, close) => {
        const fixes = values.filter((r) => r.label).map((r) => ({ label: r.label, type: "wrong", note: r.note }));
        if (!fixes.length) throw new Error("고칠 곳을 하나 이상 적어 주세요.");
        const r = await api<{ cut: Cut }>(`/api/projects/${ctx.p.id}/cuts/${c.id}/fix`, { body: { fixes } });
        close();
        cuts = [r.cut, ...cuts];
        await refreshMe();
        drawAll();
        poll();
        toast("시안을 고치기 시작했어요", "info");
      },
    });
  }

  function poll() {
    clearTimeout(pollTimer);
    if (!cuts.some((c) => c.status === "queued" || c.status === "running") || !document.body.contains(secC)) return;
    pollTimer = setTimeout(async () => {
      if (!document.body.contains(secC)) return;
      try {
        const fresh = await api<Cut[]>(`/api/projects/${ctx.p.id}/cuts?phase=stage`);
        nudgeQueued(fresh);
        const finished = fresh.filter((f) => f.status === "done" && cuts.find((c) => c.id === f.id)?.status !== "done").length;
        const changed = fresh.some((f) => cuts.find((c) => c.id === f.id)?.status !== f.status);
        cuts = fresh;
        if (changed) {
          ctx.set(await api<Project>(`/api/projects/${ctx.p.id}`));
          drawAll();
          if (finished) announce(`시안 ${finished}장이 다 됐어요`);
          if (fresh.some((c) => c.status === "failed")) await refreshMe();
        }
      } catch {
        /* 다음 차례에 */
      }
      poll();
    }, 3000);
  }

  body.append(
    h(
      "div",
      { class: "stack loose" },
      h(
        "div",
        { class: "step-intro" },
        eyebrow("p1"),
        h("div", { class: "head-title" }, h("h2", null, "무대 디자인"), helpTip("장소 위에 세울 무대를 정하는 단계예요. AI가 디자인 방향을 내고, 방향마다 시안을 한 장씩 그려요. 하나를 확정하면 그 무대가 연출컷의 기준이 돼요.")),
        h("p", { class: "small muted" }, "컨셉을 적으면 AI가 무대 시안을 여러 장 그려요"),
      ),
      resultBox,
      secA,
      secB,
      secC,
    ),
  );
  drawAll();
  poll();
}
